import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import { useUserStore } from '@/stores/userStore';

export function Header() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();

  const profile = useUserStore((state) => state.profile);
  const isBackendEnabled = useUserStore((state) => state.isBackendEnabled);
  const disableBackend = useUserStore((state) => state.disableBackend);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  async function checkAuthStatus() {
    try {
      const user = await getCurrentUser();
      setIsAuthenticated(true);
      setUserEmail(user.signInDetails?.loginId || '');
    } catch {
      setIsAuthenticated(false);
    }
  }

  const clearProfile = useUserStore((state) => state.clearProfile);

  async function handleSignOut() {
    try {
      await signOut();
      clearProfile(); // Clear profile data on sign out
      setIsAuthenticated(false);
      setShowUserMenu(false);
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  }

  return (
    <header className="bg-black border-b border-primary-500/20 sticky top-0 z-40 backdrop-blur-sm bg-black/90">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <span className="text-2xl font-bold text-primary-400 drop-shadow-[0_0_10px_rgba(34,197,94,0.3)] group-hover:drop-shadow-[0_0_15px_rgba(34,197,94,0.5)] transition-all">
              CodeQuest
            </span>
          </Link>

          {/* Navigation Links */}
          <nav className="hidden md:flex space-x-6">
            {isAuthenticated ? (
              <>
                {/* Authenticated user navigation - Tutorial, Escape Rooms, Practice, Challenges */}
                <Link
                  to="/tutorial"
                  className="text-primary-200/80 hover:text-primary-400 hover:drop-shadow-[0_0_8px_rgba(34,197,94,0.4)] transition-all"
                >
                  Tutorial
                </Link>
                <Link
                  to="/escape-room"
                  className="text-primary-200/80 hover:text-primary-400 hover:drop-shadow-[0_0_8px_rgba(34,197,94,0.4)] transition-all"
                >
                  Escape Rooms
                </Link>
                <Link
                  to="/sandbox"
                  className="text-primary-200/80 hover:text-primary-400 hover:drop-shadow-[0_0_8px_rgba(34,197,94,0.4)] transition-all"
                >
                  Practice
                </Link>
                <Link
                  to="/challenge"
                  className="text-primary-200/80 hover:text-primary-400 hover:drop-shadow-[0_0_8px_rgba(34,197,94,0.4)] transition-all"
                >
                  Challenges
                </Link>
                <Link
                  to="/leaderboard"
                  className="text-primary-200/80 hover:text-primary-400 hover:drop-shadow-[0_0_8px_rgba(34,197,94,0.4)] transition-all"
                >
                  Leaderboard
                </Link>
              </>
            ) : (
              <>
                {/* Non-authenticated user navigation - empty for now */}
              </>
            )}
          </nav>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {/* XP and Level Display - Only show when authenticated */}
            {isAuthenticated && profile && (
              <div className="hidden sm:flex items-center space-x-3 text-sm">
                <div className="px-3 py-1 bg-primary-500/10 border border-primary-500/30 rounded-lg">
                  <span className="text-primary-400 font-semibold">Level {profile.level}</span>
                </div>
                <div className="px-3 py-1 bg-primary-500/10 border border-primary-500/30 rounded-lg">
                  <span className="text-primary-300">⭐</span> <span className="text-primary-200">{profile.xp} XP</span>
                </div>
              </div>
            )}

            {/* Auth Controls */}
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 text-primary-200 hover:text-primary-400 transition-all group"
                >
                  <div className="w-8 h-8 rounded-full bg-primary-500/20 border border-primary-500/40 flex items-center justify-center group-hover:border-primary-500/60 group-hover:bg-primary-500/30 transition-all">
                    <span className="text-primary-400 font-semibold">
                      {(profile?.username || userEmail.split('@')[0] || userEmail)?.[0]?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <span className="hidden sm:block text-sm max-w-[150px] truncate">
                    {profile?.username || userEmail.split('@')[0] || userEmail}
                  </span>
                  {isBackendEnabled && (
                    <span className="text-xs text-primary-400 drop-shadow-[0_0_5px_rgba(34,197,94,0.5)]" title="Synced to cloud">
                      ☁️
                    </span>
                  )}
                </button>

                {/* Dropdown Menu */}
                {showUserMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowUserMenu(false)}
                    />
                    <div className="absolute right-0 mt-2 w-56 bg-black border border-primary-500/30 rounded-lg shadow-[0_0_20px_rgba(34,197,94,0.2)] py-2 z-50">
                      <div className="px-4 py-2 border-b border-primary-500/20">
                        <p className="text-sm text-primary-400 font-semibold truncate">
                          {profile?.username || userEmail.split('@')[0] || 'User'}
                        </p>
                        <p className="text-xs text-primary-200/60 truncate">{userEmail}</p>
                      </div>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/settings');
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-primary-200 hover:text-primary-400 hover:bg-primary-500/10 transition-all"
                      >
                        ⚙️ Settings
                      </button>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/leaderboard');
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-primary-200 hover:text-primary-400 hover:bg-primary-500/10 transition-all"
                      >
                        🏆 Leaderboard
                      </button>
                      <div className="border-t border-primary-500/20 my-1" />
                      <button
                        onClick={handleSignOut}
                        className="w-full text-left px-4 py-2 text-sm text-primary-300 hover:text-primary-400 hover:bg-primary-500/10 transition-all"
                      >
                        🚪 Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate('/login')}
                  className="text-sm text-primary-200/80 hover:text-primary-400 hover:drop-shadow-[0_0_8px_rgba(34,197,94,0.4)] transition-all"
                >
                  Sign In
                </button>
                <button
                  onClick={() => navigate('/signup')}
                  className="text-sm bg-primary-500 hover:bg-primary-600 text-black font-semibold px-4 py-2 rounded-lg hover:drop-shadow-[0_0_15px_rgba(34,197,94,0.6)] transition-all"
                >
                  Sign Up
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
